import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import DatePicker from 'react-multi-date-picker';
import { CSVLink } from 'react-csv';
import toast from 'react-hot-toast';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import ContentLoader from '../loader/ContentLoader';
import { useLoadingBar } from 'react-top-loading-bar';
import { formatSupplierInfo } from '../../helper/commonHelper';
import { inventoriesService } from '../../services/inventories.service';
import { airlinesService } from '../../services/airlines.service';
import { airportsService } from '../../services/airports.service';
import { suppliersService } from '../../services/suppliers.service';
import { SOMETHING_WENT_WRONG } from '../../constants/strings';

const STATUS_FILTER_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

const selectStyles = {
  control: (base, state) => ({
    ...base, borderRadius: 8, border: "1px solid #e8edf5", background: "#f8f9fe", minHeight: 34, height: 34,
    fontSize: 13, boxShadow: "none", "&:hover": { borderColor: state.isFocused ? "#667eea" : "#e8edf5" }
  }),
  valueContainer: (base) => ({ ...base, padding: "0 8px" }),
  indicatorsContainer: (base) => ({ ...base, height: 34 }),
  placeholder: (base) => ({ ...base, color: "#a0a8b8", fontSize: 13 }),
  menuPortal: (base) => ({ ...base, zIndex: 2000 }),
};

const toArray = (resData) => (Array.isArray(resData) ? resData : resData?.data || []);

const initialFilters = {
  dep_airport_code: "", arr_airport_code: "", departure_date: "", arrival_date: "",
  supplier: "", pnr: "", airline_code: "", flight_number: "", status: ""
};

const ListInventory = () => {
  const { start, complete } = useLoadingBar({ color: "blue", height: 2 });

  const [loading, setLoading] = useState(false);
  const [inventoryRes, setInventoryRes] = useState({
    pagination: {},
    inventories: []
  });

  const [pageValue, setPageValue] = useState(1);
  const [pagingLimit, setPagingLimit] = useState("10");
  const [goPage, setGoPage] = useState("");

  const [filters, setFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);
  const [airlineOptions, setAirlineOptions] = useState([]);

  useEffect(() => {
    loadInventory();
  }, [pagingLimit, pageValue, appliedFilters])

  useEffect(() => {
    loadAirlines();
  }, [])

  const showSwal = (title, text, icon) => {
    withReactContent(Swal).fire({ title, text, icon })
  }

  const loadInventory = async () => {
    try {
      setLoading(true);
      start();
      const res = await inventoriesService.list({
        page: Number(pageValue),
        limit: Number(pagingLimit),
        ...appliedFilters,
      });
      if (res?.success) {
        setInventoryRes({
          pagination: res.resData?.pagination || {},
          inventories: res.resData?.data || []
        })
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error")
    } finally {
      setLoading(false);
      complete();
    }
  }

  // Airline dropdown: airlines > list (status = active); value is the airline code the inventory API filters on
  const loadAirlines = async () => {
    try {
      const res = await airlinesService.list({ page: 1, status: "active" });
      if (res?.success) {
        setAirlineOptions(toArray(res.resData).map(a => ({ value: a.airline_code, label: `${a.airline_name} (${a.airline_code})` })));
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error")
    }
  }

  // Departure / Arrival airport dropdowns: airports > list (status = active), searched on the server
  const loadAirports = async (search = "") => {
    try {
      const res = await airportsService.list({ page: 1, limit: 50, status: "active", ...(search ? { search } : {}) });
      if (res?.success) {
        return toArray(res.resData).map(a => ({
          value: a.airport_code,
          label: `${a.airport_name} (${a.airport_code}), ${a.city_name}`
        }));
      }
      toast.error(res?.message);
      return [];
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error")
      return [];
    }
  }

  // Supplier dropdown: suppliers > list (status = active), searched on the server; value is the agency name the inventory API filters on
  const loadSuppliers = async (search = "") => {
    try {
      const res = await suppliersService.list({ offset: 0, limit: 20, status: "active", ...(search ? { search } : {}) });
      if (res?.success) {
        return (res.resData?.data || []).map(s => ({ value: s.agency_name, label: s.agency_name }));
      }
      toast.error(res?.message);
      return [];
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error")
      return [];
    }
  }

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  }

  const handleApply = (e) => {
    e?.preventDefault();
    setPageValue(1);
    setAppliedFilters({ ...filters });
  }

  const handleReset = () => {
    setFilters(initialFilters);
    setPageValue(1);
    setAppliedFilters(initialFilters);
  }

  const handlePagingLimit = (e) => {
    setPageValue(1);
    setPagingLimit(e.target.value);
  }

  const handlePagination = (pageValueInput) => {
    setPageValue(pageValueInput);
  }

  const handleGoPage = () => {
    const n = Number(goPage);
    if (n >= 1 && n <= totalPage) {
      setPageValue(n);
    }
    setGoPage("");
  }

  // No update/delete endpoints exist for inventory yet
  const handleActionNotAvailable = () => {
    toast("This action is not available yet");
  }

  const inventories = inventoryRes?.inventories || [];
  const totalRecords = inventoryRes?.pagination?.total || 0;
  const totalPage = Math.max(1, inventoryRes?.pagination?.totalPages || 1);
  const fromRecord = totalRecords === 0 ? 0 : (pageValue - 1) * Number(pagingLimit) + 1;
  const toRecord = Math.min(pageValue * Number(pagingLimit), totalRecords);

  const getPageNumbers = () => {
    if (totalPage <= 5) return Array.from({ length: totalPage }, (_, i) => i + 1);
    const sorted = [...new Set([1, 2, totalPage - 1, totalPage, pageValue])]
      .filter(n => n >= 1 && n <= totalPage).sort((a, b) => a - b);
    const out = [];
    sorted.forEach((n, i) => {
      if (i > 0 && n - sorted[i - 1] > 1) out.push("...");
      out.push(n);
    });
    return out;
  }

  const pageBtn = (active, disabled) => ({
    minWidth: 32, height: 32, padding: "0 8px", borderRadius: 8,
    border: active ? "1px solid #0d6efd" : "1px solid #e8edf5",
    background: active ? "#0d6efd" : "#fff",
    color: active ? "#fff" : disabled ? "#c3cad8" : "#444",
    fontSize: 13, fontWeight: 600, cursor: disabled ? "default" : "pointer"
  });

  const csvHeaders = [
    { label: "Inventory ID", key: "inventory_id" },
    { label: "Supplier", key: "supplier_info" },
    { label: "Departure", key: "dep_airport_code" },
    { label: "Arrival", key: "arr_airport_code" },
    { label: "Departure Date", key: "dep_date" },
    { label: "Departure Time", key: "dep_time" },
    { label: "Arrival Time", key: "arr_time" },
    { label: "Airline", key: "airline_name" },
    { label: "Flight No", key: "flight_number" },
    { label: "PNR", key: "pnr" },
    { label: "Seats", key: "seat_quantity" },
  ];

  const filterInput = { borderRadius: 8, border: "1px solid #e8edf5", background: "#f8f9fe", fontSize: 13, height: 34 };

  return (
    <>
      <section className="section dashboard">

        {/* ── Page header ── */}
        <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
          <div>
            <h4 className="m-0 fw-bold" style={{ color: "#1a202c", fontSize: 20 }}>Inventory</h4>
            <nav className="d-flex align-items-center gap-1 mt-1" style={{ fontSize: 12, color: "#8a9ab5" }}>
              <Link to="/" style={{ color: "#8a9ab5" }}><i className="bi bi-house-door"></i></Link>
              <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
              <span>Inventory</span>
              <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
              <span className="fw-semibold" style={{ color: "#4a5568" }}>Inventory List</span>
            </nav>
          </div>
          <div className="d-flex align-items-center gap-2">
            <Link to="/inventory/add" className="btn btn-sm d-flex align-items-center gap-1 text-white px-3" style={{ background: "#4f3df5", borderRadius: 8, height: 34, fontSize: 13, fontWeight: 600 }}>
              <i className="bi bi-plus-lg"></i> Add Inventory
            </Link>
            <CSVLink
              data={inventories.map(i => ({ ...i, supplier_info: formatSupplierInfo(i.supplier_info) }))}
              headers={csvHeaders}
              filename="inventory.csv"
              className="btn btn-sm d-flex align-items-center gap-1 px-3"
              style={{ background: "#fff", border: "1px solid #e8edf5", borderRadius: 8, height: 34, fontSize: 13, color: "#444" }}
            >
              <i className="bi bi-download"></i> Export
            </CSVLink>
          </div>
        </div>

        {/* ── Filter Card ── */}
        <form onSubmit={handleApply} className="card border-0 shadow-sm mb-3" style={{ borderRadius: 14 }}>
          <div className="card-body p-3">
            <div className="row g-2 align-items-center">
              <div className="col-12 col-md-3">
                <AsyncSelect
                  cacheOptions
                  defaultOptions
                  loadOptions={loadAirports}
                  onChange={(o) => setFilters({ ...filters, dep_airport_code: o ? o.value : "" })}
                  placeholder="Departure Airport"
                  isClearable
                  menuPortalTarget={document.body}
                  styles={selectStyles}
                />
              </div>
              <div className="col-12 col-md-3">
                <AsyncSelect
                  cacheOptions
                  defaultOptions
                  loadOptions={loadAirports}
                  onChange={(o) => setFilters({ ...filters, arr_airport_code: o ? o.value : "" })}
                  placeholder="Arrival Airport"
                  isClearable
                  menuPortalTarget={document.body}
                  styles={selectStyles}
                />
              </div>
              <div className="col-6 col-md-3">
                <DatePicker
                  value={filters.departure_date || ""}
                  onChange={(d) => setFilters({ ...filters, departure_date: d ? d.format("YYYY-MM-DD") : "" })}
                  format="YYYY-MM-DD"
                  placeholder="Departure date"
                  editable={false}
                  inputClass="form-control"
                  containerStyle={{ width: "100%" }}
                  style={{ ...filterInput, width: "100%" }}
                />
              </div>
              <div className="col-6 col-md-3">
                <DatePicker
                  value={filters.arrival_date || ""}
                  onChange={(d) => setFilters({ ...filters, arrival_date: d ? d.format("YYYY-MM-DD") : "" })}
                  format="YYYY-MM-DD"
                  placeholder="Arrival date"
                  editable={false}
                  inputClass="form-control"
                  containerStyle={{ width: "100%" }}
                  style={{ ...filterInput, width: "100%" }}
                />
              </div>
              <div className="col-6 col-md-2">
                <AsyncSelect
                  cacheOptions
                  defaultOptions
                  loadOptions={loadSuppliers}
                  value={filters.supplier ? { value: filters.supplier, label: filters.supplier } : null}
                  onChange={(o) => setFilters({ ...filters, supplier: o ? o.value : "" })}
                  placeholder="Select Supplier"
                  isClearable
                  menuPortalTarget={document.body}
                  styles={selectStyles}
                />
              </div>
              <div className="col-6 col-md-2">
                <input type="text" className="form-control" name="pnr" placeholder="PNR" value={filters.pnr} onChange={handleFilterChange} style={filterInput} />
              </div>
              <div className="col-6 col-md-2">
                <Select
                  options={airlineOptions}
                  value={airlineOptions.find(o => o.value === filters.airline_code) || null}
                  onChange={(o) => setFilters({ ...filters, airline_code: o ? o.value : "" })}
                  placeholder="Select Airline"
                  isClearable
                  menuPortalTarget={document.body}
                  styles={selectStyles}
                />
              </div>
              <div className="col-6 col-md-2">
                <input type="text" className="form-control" name="flight_number" placeholder="Flight No." value={filters.flight_number} onChange={handleFilterChange} style={filterInput} />
              </div>
              <div className="col-6 col-md-2">
                <Select
                  options={STATUS_FILTER_OPTIONS}
                  value={STATUS_FILTER_OPTIONS.find(o => o.value === filters.status) || null}
                  onChange={(o) => setFilters({ ...filters, status: o ? o.value : "" })}
                  placeholder="Select Status"
                  isClearable
                  menuPortalTarget={document.body}
                  styles={selectStyles}
                />
              </div>
              <div className="col-6 col-md-2 d-flex gap-2">
                <button type="submit" className="btn btn-sm d-flex align-items-center gap-1 text-white px-3" style={{ background: "#4f3df5", borderRadius: 8, height: 34, fontSize: 13, fontWeight: 600 }}>
                  <i className="bi bi-search"></i> Apply
                </button>
                <button type="button" onClick={handleReset} className="btn btn-sm d-flex align-items-center gap-1 px-3" style={{ background: "#fff", border: "1px solid #e8edf5", borderRadius: 8, height: 34, fontSize: 13, color: "#444" }}>
                  <i className="bi bi-arrow-counterclockwise"></i> Reset
                </button>
              </div>
            </div>
          </div>
        </form>

        {/* ── Table Card ── */}
      <div className="card border-0 shadow-sm" style={{ borderRadius: 14, overflow: "hidden" }}>
          <div>

                {/* Card Header */}
                {/* <div className="d-flex align-items-center justify-content-between mb-3">
                  <div className="d-flex align-items-center gap-2">
                    <div style={{
                      width: "34px", height: "34px", borderRadius: "9px",
                      background: "linear-gradient(135deg, #0f2557, #1a3a8f)",
                      display: "flex", alignItems: "center", justifyContent: "center"
                    }}>
                      <i className="bi bi-airplane-fill text-white" style={{ fontSize: "14px" }}></i>
                    </div>
                    <div>
                      <h6 className="mb-0 fw-bold" style={{ color: "#0f2557", fontSize: "14px" }}>Inventory List</h6>
                      <span style={{ fontSize: "11px", color: "#8a9ab5" }}>
                        Total: <strong style={{ color: "#1a3a8f" }}>{inventoryRes?.pagination?.total ?? 0}</strong> records
                      </span>
                    </div>
                  </div>
                  <Link to="/inventory/add" style={{ textDecoration: "none" }}>
                    <button className="btn btn-sm fw-semibold d-flex align-items-center gap-2 text-white"
                      style={{
                        background: "linear-gradient(135deg, #0f2557, #1a3a8f)",
                        borderRadius: "8px", border: "none",
                        boxShadow: "0 4px 12px rgba(15,37,87,0.25)",
                        fontSize: "13px", height: "34px", padding: "0 16px"
                      }}>
                      <i className="bi bi-plus-circle-fill"></i> Add New PNR
                    </button>
                  </Link>
                </div> */}

                {/* Loading Overlay */}
                {loading && (
                  <div style={{
                    position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
                    background: "rgba(255,255,255,0.7)",
                    display: "flex", justifyContent: "center", alignItems: "center",
                    zIndex: 10, borderRadius: "14px"
                  }}>
                    <ContentLoader loaderType="circleSpin" />
                  </div>
                )}

                {/* Table */}
                <div className="table-responsive">
                  <table className="table table-hover mb-0" style={{ fontSize: "13px" }}>
                    <thead>
                      <tr style={{ background: "#f4f6fb" }}>
                        {["Supplier", "Departure", "Arrival", "Dept – Arr Time", "Airline (Flight No)", "PNR", "Dept. Date", "Seats", "Fare", "+/- Seat", "Action"].map((h, i) => (
                          <th key={i} style={{
                            fontSize: "11px", fontWeight: "700", color: "#8a9ab5",
                            textTransform: "uppercase", letterSpacing: "0.6px",
                            border: "none", padding: "10px 12px", whiteSpace: "nowrap",
                            background: "#f4f6fb"
                          }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {inventories.length > 0 ? (
                        inventories.map((inventory, idx) => (
                          <tr
                            key={`${inventory?.inventory_id}-${inventory?.pnr}-${idx}`}
                            style={{ borderBottom: "1px solid #f0f2f8", verticalAlign: "middle" }}
                            onMouseEnter={(e) => (e.currentTarget.style.background = "#f8f9fe")}
                            onMouseLeave={(e) => (e.currentTarget.style.background = "")}
                          >
                            {/* Supplier */}
                            <td style={{ padding: "10px 12px" }}>
                              <div className="d-flex align-items-start gap-2">
                                <div
                                  style={{
                                    width: "26px",
                                    height: "26px",
                                    borderRadius: "7px",
                                    background: "#eef0ff",
                                    flexShrink: 0,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center"
                                  }}
                                >
                                  <i className="bi bi-building" style={{ fontSize: "11px", color: "#1a3a8f" }}></i>
                                </div>
                                <div>
                                  <div style={{ fontWeight: "700", color: "#0f2557", fontSize: "12px" }}>
                                    Inventory #{inventory?.inventory_id}
                                  </div>
                                  <div style={{ fontSize: "11px", color: "#6b7280" }}>
                                    {formatSupplierInfo(inventory?.supplier_info)}
                                  </div>
                                </div>
                              </div>
                            </td>

                            {/* Departure */}
                            <td style={{ padding: "10px 12px" }}>
                              <span style={{ fontWeight: "600", color: "#0f2557", fontSize: "12px" }}>
                                {inventory?.dep_city_name || "-"}
                              </span>
                              <span
                                style={{
                                  marginLeft: "4px",
                                  fontSize: "10px",
                                  fontWeight: "700",
                                  background: "#eef0ff",
                                  color: "#1a3a8f",
                                  borderRadius: "5px",
                                  padding: "1px 6px"
                                }}
                              >
                                {inventory?.dep_airport_code || "-"}
                              </span>
                            </td>

                            {/* Arrival */}
                            <td style={{ padding: "10px 12px" }}>
                              <span style={{ fontWeight: "600", color: "#0f2557", fontSize: "12px" }}>
                                {inventory?.arr_city_name || "-"}
                              </span>
                              <span
                                style={{
                                  marginLeft: "4px",
                                  fontSize: "10px",
                                  fontWeight: "700",
                                  background: "#eef0ff",
                                  color: "#1a3a8f",
                                  borderRadius: "5px",
                                  padding: "1px 6px"
                                }}
                              >
                                {inventory?.arr_airport_code || "-"}
                              </span>
                            </td>

                            {/* Dept – Arr Time */}
                            <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>
                              <span style={{ fontSize: "12px", color: "#374151", fontWeight: "600" }}>
                                <i className="bi bi-clock me-1" style={{ color: "#8a9ab5", fontSize: "10px" }}></i>
                                {inventory?.dep_time || "-"}
                              </span>
                              <span style={{ margin: "0 4px", color: "#8a9ab5" }}>→</span>
                              <span style={{ fontSize: "12px", color: "#374151", fontWeight: "600" }}>
                                {inventory?.arr_time || "-"}
                              </span>
                            </td>

                            {/* Airline (Flight No) */}
                            <td style={{ padding: "10px 12px" }}>
                              <div className="d-flex align-items-center gap-2">
                                <img
                                  style={{
                                    width: "22px",
                                    height: "22px",
                                    borderRadius: "5px",
                                    objectFit: "contain",
                                    background: "#f4f6fb",
                                    padding: "2px"
                                  }}
                                  src={`https://images.kiwi.com/airlines/32/${inventory?.airline_code}.png`}
                                  alt={inventory?.airline_code || "airline"}
                                />
                                <div>
                                  <div style={{ fontWeight: "700", color: "#374151", fontSize: "12px", lineHeight: 1.1 }}>
                                    {inventory?.airline_name || "-"}
                                  </div>
                                  <div style={{ fontSize: "11px", color: "#6b7280" }}>
                                    {inventory?.flight_number || "-"}
                                  </div>
                                </div>
                              </div>
                            </td>

                            {/* PNR */}
                            <td style={{ padding: "10px 12px" }}>
                              <span
                                style={{
                                  background: "#f4f6fb",
                                  color: "#0f2557",
                                  borderRadius: "6px",
                                  padding: "3px 8px",
                                  fontSize: "11px",
                                  fontWeight: "700",
                                  fontFamily: "monospace"
                                }}
                              >
                                {inventory?.pnr || "-"}
                              </span>
                            </td>

                            {/* Dept. Date */}
                            <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>
                              <span
                                style={{
                                  fontSize: "11px",
                                  color: "#6b7280",
                                  fontWeight: "600",
                                  background: "#f4f6fb",
                                  borderRadius: "6px",
                                  padding: "2px 8px"
                                }}
                              >
                                <i className="bi bi-calendar3 me-1" style={{ color: "#8a9ab5" }}></i>
                                {inventory?.dep_date || "-"}
                              </span>
                            </td>

                            {/* Seats */}
                            <td style={{ padding: "10px 12px" }}>
                              <span
                                style={{
                                  background: "#ecfdf5",
                                  color: "#065f46",
                                  borderRadius: "6px",
                                  padding: "3px 8px",
                                  fontSize: "11px",
                                  fontWeight: "700"
                                }}
                              >
                                <i className="bi bi-person-fill me-1" style={{ fontSize: "10px" }}></i>
                                {inventory?.seat_quantity ?? 0}
                              </span>
                            </td>

                            {/* Fare */}
                            <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>
                              <button
                                type="button"
                                style={{
                                  background: "#eef0ff",
                                  border: "none",
                                  borderRadius: "7px",
                                  width: "28px",
                                  height: "28px",
                                  padding: 0,
                                  marginRight: "4px"
                                }}
                                data-bs-toggle="modal"
                                data-bs-target="#ViewFare"
                                title="View Fare"
                                onMouseEnter={(e) => (e.currentTarget.style.background = "#dde3ef")}
                                onMouseLeave={(e) => (e.currentTarget.style.background = "#eef0ff")}
                              >
                                <i className="bi bi-eye-fill" style={{ fontSize: "11px", color: "#1a3a8f" }}></i>
                              </button>
                              <button
                                type="button"
                                style={{
                                  background: "#fffbeb",
                                  border: "none",
                                  borderRadius: "7px",
                                  width: "28px",
                                  height: "28px",
                                  padding: 0
                                }}
                                data-bs-toggle="modal"
                                data-bs-target="#UpdateFare"
                                title="Update Fare"
                                onMouseEnter={(e) => (e.currentTarget.style.background = "#fef3c7")}
                                onMouseLeave={(e) => (e.currentTarget.style.background = "#fffbeb")}
                              >
                                <i className="bi bi-pencil-fill" style={{ fontSize: "11px", color: "#b45309" }}></i>
                              </button>
                            </td>

                            {/* +/- Seat */}
                            <td style={{ padding: "10px 12px" }}>
                              <button
                                type="button"
                                style={{
                                  background: "#ecfdf5",
                                  border: "none",
                                  borderRadius: "7px",
                                  width: "28px",
                                  height: "28px",
                                  padding: 0
                                }}
                                data-bs-toggle="modal"
                                data-bs-target="#AddUpdateSeat"
                                title="Add / Remove Seats"
                                onMouseEnter={(e) => (e.currentTarget.style.background = "#d1fae5")}
                                onMouseLeave={(e) => (e.currentTarget.style.background = "#ecfdf5")}
                              >
                                <i className="bi bi-pencil-square" style={{ fontSize: "11px", color: "#065f46" }}></i>
                              </button>
                            </td>

                            {/* Action */}
                            <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>
                              <button
                                className="btn btn-sm me-1"
                                style={{
                                  background: "#eef0ff",
                                  color: "#1a3a8f",
                                  border: "none",
                                  borderRadius: "7px",
                                  width: "28px",
                                  height: "28px",
                                  padding: 0
                                }}
                                title="Edit"
                                onClick={handleActionNotAvailable}
                              >
                                <i className="bi bi-pencil-fill" style={{ fontSize: "11px" }}></i>
                              </button>

                              <button
                                className="btn btn-sm me-1"
                                style={{
                                  background: "#ecfdf5",
                                  color: "#065f46",
                                  border: "none",
                                  borderRadius: "7px",
                                  width: "28px",
                                  height: "28px",
                                  padding: 0
                                }}
                                title="Refresh"
                                onClick={loadInventory}
                              >
                                <i className="bi bi-arrow-clockwise" style={{ fontSize: "11px" }}></i>
                              </button>

                              <button
                                className="btn btn-sm"
                                style={{
                                  background: "#fff1f1",
                                  color: "#dc2626",
                                  border: "none",
                                  borderRadius: "7px",
                                  width: "28px",
                                  height: "28px",
                                  padding: 0
                                }}
                                title="Delete"
                                onClick={handleActionNotAvailable}
                              >
                                <i className="bi bi-trash-fill" style={{ fontSize: "11px" }}></i>
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="11" className="text-center py-5">
                            <div style={{ color: "#8a9ab5" }}>
                              <i
                                className="bi bi-inbox"
                                style={{ fontSize: "36px", display: "block", marginBottom: "8px" }}
                              ></i>
                              <span style={{ fontSize: "13px" }}>No inventory found</span>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 py-3" style={{ borderTop: "1px solid #eef1f6", fontSize: 13, color: "#6b7a90" }}>
                  <div>Showing <b>{fromRecord}–{toRecord}</b> of <b>{totalRecords}</b> inventories</div>
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <span>Rows</span>
                    <select className="form-select form-select-sm" style={{ width: 95, borderRadius: 8 }} value={pagingLimit} onChange={handlePagingLimit}>
                      {["10", "20", "50", "100", "500"].map(v => <option key={v} value={v}>{v}/page</option>)}
                    </select>
                    <span>Go</span>
                    <input type="number" min="1" max={totalPage} className="form-control form-control-sm text-center" style={{ width: 60, borderRadius: 8 }}
                      value={goPage} onChange={(e) => setGoPage(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleGoPage()} placeholder={String(pageValue)} />
                    <span>/ {totalPage}</span>
                    <button type="button" style={pageBtn(false, false)} onClick={handleGoPage}>Go</button>
                    <div className="d-flex align-items-center gap-1">
                      <button type="button" disabled={pageValue <= 1} style={pageBtn(false, pageValue <= 1)} onClick={() => handlePagination(1)}><i className="bi bi-chevron-double-left"></i></button>
                      <button type="button" disabled={pageValue <= 1} style={pageBtn(false, pageValue <= 1)} onClick={() => handlePagination(pageValue - 1)}><i className="bi bi-chevron-left"></i></button>
                      {getPageNumbers().map((n, i) => n === "..."
                        ? <span key={"e" + i} className="px-1">...</span>
                        : <button type="button" key={n} style={pageBtn(n === pageValue, false)} onClick={() => handlePagination(n)}>{n}</button>)}
                      <button type="button" disabled={pageValue >= totalPage} style={pageBtn(false, pageValue >= totalPage)} onClick={() => handlePagination(pageValue + 1)}><i className="bi bi-chevron-right"></i></button>
                      <button type="button" disabled={pageValue >= totalPage} style={pageBtn(false, pageValue >= totalPage)} onClick={() => handlePagination(totalPage)}><i className="bi bi-chevron-double-right"></i></button>
                    </div>
                  </div>
                </div>

              </div>
            </div>
      </section>

      {/* ── View Fare Modal ── */}
      <div className="modal fade" id="ViewFare" tabIndex="-1">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content border-0 shadow" style={{ borderRadius: "16px", overflow: "hidden" }}>
            <div style={{ height: "4px", background: "linear-gradient(135deg, #0f2557, #1a3a8f)" }}></div>
            <div className="modal-header border-0 pb-0 px-4 pt-4">
              <div className="d-flex align-items-center gap-3">
                <div style={{ width: "38px", height: "38px", borderRadius: "10px", background: "linear-gradient(135deg, #0f2557, #1a3a8f)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <i className="bi bi-eye-fill text-white" style={{ fontSize: "15px" }}></i>
                </div>
                <div>
                  <h6 className="mb-0 fw-bold" style={{ color: "#0f2557", fontSize: "15px" }}>Fare Details</h6>
                  <p className="mb-0" style={{ fontSize: "11px", color: "#8a9ab5" }}>View current fare slabs</p>
                </div>
              </div>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div className="modal-body px-4 pb-4">
              <table className="table mb-0" style={{ fontSize: "13px" }}>
                <thead>
                  <tr style={{ background: "#f4f6fb" }}>
                    {["Fare", "Sold Seat", "Unsold Seat", "Total Seat", "Departure Date"].map((h, i) => (
                      <th key={i} style={{ fontSize: "11px", fontWeight: "700", color: "#8a9ab5", textTransform: "uppercase", letterSpacing: "0.6px", border: "none", padding: "10px 12px", background: "#f4f6fb" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[1, 2].map((_, i) => (
                    <tr key={i} style={{ borderBottom: "1px solid #f0f2f8", verticalAlign: "middle" }}>
                      <td style={{ padding: "10px 12px" }}>
                        <span style={{ background: "#ecfdf5", color: "#065f46", borderRadius: "6px", padding: "3px 8px", fontSize: "12px", fontWeight: "700" }}>₹ 5,000</span>
                      </td>
                      <td style={{ padding: "10px 12px", color: "#374151", fontWeight: "600" }}>5</td>
                      <td style={{ padding: "10px 12px", color: "#374151", fontWeight: "600" }}>5</td>
                      <td style={{ padding: "10px 12px" }}>
                        <span style={{ background: "#eef0ff", color: "#1a3a8f", borderRadius: "6px", padding: "2px 8px", fontSize: "11px", fontWeight: "700" }}>10</span>
                      </td>
                      <td style={{ padding: "10px 12px" }}>
                        <span style={{ fontSize: "11px", color: "#6b7280", fontWeight: "600", background: "#f4f6fb", borderRadius: "6px", padding: "2px 8px" }}>
                          <i className="bi bi-calendar3 me-1" style={{ color: "#8a9ab5" }}></i>31 July 2025
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* ── Update Fare Modal ── */}
      <div className="modal fade" id="UpdateFare" tabIndex="-1">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content border-0 shadow" style={{ borderRadius: "16px", overflow: "hidden" }}>
            <div style={{ height: "4px", background: "linear-gradient(135deg, #0f2557, #1a3a8f)" }}></div>
            <div className="modal-header border-0 pb-0 px-4 pt-4">
              <div className="d-flex align-items-center gap-3">
                <div style={{ width: "38px", height: "38px", borderRadius: "10px", background: "linear-gradient(135deg, #0f2557, #1a3a8f)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <i className="bi bi-pencil-fill text-white" style={{ fontSize: "15px" }}></i>
                </div>
                <div>
                  <h6 className="mb-0 fw-bold" style={{ color: "#0f2557", fontSize: "15px" }}>Update Fare</h6>
                  <p className="mb-0" style={{ fontSize: "11px", color: "#8a9ab5" }}>Modify fare details for this PNR</p>
                </div>
              </div>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div className="modal-body px-4 pb-4">

              {/* PNR + Warning */}
              <div className="row g-2 mb-3">
                <div className="col-md-4">
                  <label className="form-label mb-1" style={{ fontSize: "11px", fontWeight: "700", color: "#8a9ab5", textTransform: "uppercase", letterSpacing: "0.6px" }}>PNR Number</label>
                  <div className="input-group input-group-sm">
                    <span className="input-group-text" style={{ background: "#eef0ff", border: "1.5px solid #dde3ef", borderRight: "none", borderRadius: "8px 0 0 8px" }}>
                      <i className="bi bi-upc" style={{ color: "#1a3a8f", fontSize: "12px" }}></i>
                    </span>
                    <input type="text" className="form-control form-control-sm" id="txtUpdate_PnrNumber" value="GFg123" readOnly
                      style={{ border: "1.5px solid #dde3ef", borderLeft: "none", borderRadius: "0 8px 8px 0", background: "#f8f9fe", fontSize: "13px", fontWeight: "700" }} />
                  </div>
                </div>
                <div className="col-md-8 d-flex align-items-end">
                  <div className="d-flex align-items-center gap-2 p-2 rounded-3 w-100" style={{ background: "#fff1f1", border: "1.5px solid #fca5a5" }}>
                    <i className="bi bi-exclamation-circle-fill" style={{ color: "#dc2626", fontSize: "13px", flexShrink: 0 }}></i>
                    <span style={{ fontSize: "11px", color: "#dc2626", fontWeight: "500" }}>Please select fare slab first, before new update!</span>
                  </div>
                </div>
              </div>

              {/* Existing Info */}
              <div className="p-3 mb-3 rounded-3" style={{ background: "#f8f9fe", border: "1px solid #eef0f8" }}>
                <div className="d-flex align-items-center gap-2 mb-3">
                  <i className="bi bi-info-circle-fill" style={{ color: "#1a3a8f", fontSize: "13px" }}></i>
                  <span style={{ fontSize: "11px", fontWeight: "700", color: "#1a3a8f", textTransform: "uppercase", letterSpacing: "0.7px" }}>Existing Info</span>
                </div>
                <div className="row g-2">
                  {[
                    { id: "txtUpdate_FareSlab", label: "Fare Slab", type: "select", options: [{ v: "5000", l: "₹ 5,000" }] },
                    { id: "txtUpdate_SeatLeft", label: "Seat Left", type: "text", value: 0, readOnly: true },
                    { id: "txtUpdate_SeatSold", label: "Seat Sold", type: "text", value: 0, readOnly: true },
                    { id: "txtUpdate_InfantFare", label: "Infant Fare", type: "text", value: 0, readOnly: true },
                    { id: "txtUpdate_TotalFare", label: "Total Fare", type: "text", value: 0, readOnly: true },
                    { id: "txtUpdate_TotalMarkupFare", label: "Markup Fare", type: "text", value: 0, readOnly: true },
                  ].map(({ id, label, type, options, value, readOnly }) => (
                    <div className="col-md-4" key={id}>
                      <label className="form-label mb-1" style={{ fontSize: "11px", fontWeight: "700", color: "#8a9ab5", textTransform: "uppercase", letterSpacing: "0.6px" }}>{label}</label>
                      {type === "select" ? (
                        <select className="form-select form-select-sm" id={id}
                          style={{ borderRadius: "8px", border: "1.5px solid #dde3ef", fontSize: "13px" }}>
                          <option value="">Select</option>
                          {options.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                        </select>
                      ) : (
                        <input type="text" className="form-control form-control-sm" id={id} value={value} readOnly={readOnly}
                          style={{ borderRadius: "8px", border: "1.5px solid #dde3ef", background: readOnly ? "#f8f9fe" : "white", fontSize: "13px" }} />
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* New Fare */}
              <div className="p-3 rounded-3" style={{ background: "#f8f9fe", border: "1px solid #eef0f8" }}>
                <div className="d-flex align-items-center gap-2 mb-3">
                  <i className="bi bi-pencil-square" style={{ color: "#1a3a8f", fontSize: "13px" }}></i>
                  <span style={{ fontSize: "11px", fontWeight: "700", color: "#1a3a8f", textTransform: "uppercase", letterSpacing: "0.7px" }}>New Fare</span>
                </div>
                <div className="row g-2 align-items-end">
                  {[
                    { id: "txtUpdate_SelectSeat", label: "Seat", type: "select", options: [1, 2, 3, 4].map(v => ({ v, l: v })), col: 2 },
                    { id: "txtUpdate_BasicFare", label: "Basic Fare", type: "text", value: 0, col: 2 },
                    { id: "txtUpdate_YQFare", label: "YQ Fare", type: "text", value: 0, col: 2 },
                    { id: "txtUpdate_YRFare", label: "YR Fare", type: "text", value: 0, col: 2 },
                    { id: "txtUpdate_OTFare", label: "OT Fare", type: "text", value: 0, col: 2 },
                    { id: "txtUpdate_InfantFareNew", label: "Infant Fare", type: "text", value: 0, col: 3 },
                    { id: "txtUpdate_GrandTotalFare", label: "Grand Total", type: "text", value: 0, readOnly: true, col: 3 },
                  ].map(({ id, label, type, options, value, readOnly, col }) => (
                    <div className={`col-md-${col}`} key={id}>
                      <label className="form-label mb-1" style={{ fontSize: "11px", fontWeight: "700", color: "#8a9ab5", textTransform: "uppercase", letterSpacing: "0.6px" }}>{label}</label>
                      {type === "select" ? (
                        <select className="form-select form-select-sm" id={id}
                          style={{ borderRadius: "8px", border: "1.5px solid #dde3ef", fontSize: "13px" }}>
                          <option value="">Select</option>
                          {options.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                        </select>
                      ) : (
                        <input type="text" className="form-control form-control-sm" id={id} value={value} readOnly={readOnly}
                          style={{ borderRadius: "8px", border: "1.5px solid #dde3ef", background: readOnly ? "#f8f9fe" : "white", fontSize: "13px" }} />
                      )}
                    </div>
                  ))}
                  <div className="col-md-3 d-flex align-items-end">
                    <button type="submit" className="btn btn-sm fw-semibold d-flex align-items-center gap-2 text-white w-100 justify-content-center"
                      style={{ background: "linear-gradient(135deg, #0f2557, #1a3a8f)", borderRadius: "8px", border: "none", boxShadow: "0 4px 12px rgba(15,37,87,0.25)", fontSize: "13px", height: "34px" }}>
                      <i className="bi bi-floppy-fill"></i> Update
                    </button>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>

      {/* ── Add/Update Seat Modal ── */}
      <div className="modal fade" id="AddUpdateSeat" tabIndex="-1">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content border-0 shadow" style={{ borderRadius: "16px", overflow: "hidden" }}>
            <div style={{ height: "4px", background: "linear-gradient(135deg, #0f2557, #1a3a8f)" }}></div>
            <div className="modal-header border-0 pb-0 px-4 pt-4">
              <div className="d-flex align-items-center gap-3">
                <div style={{ width: "38px", height: "38px", borderRadius: "10px", background: "linear-gradient(135deg, #0f2557, #1a3a8f)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <i className="bi bi-person-lines-fill text-white" style={{ fontSize: "15px" }}></i>
                </div>
                <div>
                  <h6 className="mb-0 fw-bold" style={{ color: "#0f2557", fontSize: "15px" }}>Seat Details</h6>
                  <p className="mb-0" style={{ fontSize: "11px", color: "#8a9ab5" }}>Add or decrease seats for PNR</p>
                </div>
              </div>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div className="modal-body px-4 pb-4">

              {/* Add Seat Section */}
              <div className="p-3 mb-3 rounded-3" style={{ background: "#f8f9fe", border: "1px solid #eef0f8" }}>
                <div className="d-flex align-items-center gap-2 mb-3">
                  <i className="bi bi-plus-circle-fill" style={{ color: "#065f46", fontSize: "13px" }}></i>
                  <span style={{ fontSize: "11px", fontWeight: "700", color: "#065f46", textTransform: "uppercase", letterSpacing: "0.7px" }}>Add New Seat</span>
                  <span style={{ fontSize: "10px", fontWeight: "700", background: "#eef0ff", color: "#1a3a8f", borderRadius: "5px", padding: "1px 7px" }}>PNR: ABC123</span>
                </div>
                <div className="row g-2 align-items-end">
                  {[
                    { id: "txtUpdate_AddSeat_SeatNumber", label: "Seat No.", value: "0", col: 2 },
                    { id: "txtUpdate_AddSeat_BasicFare", label: "Basic Fare", value: "0", col: 2 },
                    { id: "txtUpdate_AddSeat_YQFare", label: "YQ Fare", value: "0", col: 2 },
                    { id: "txtUpdate_AddSeat_YRFare", label: "YR Fare", value: "0", col: 2 },
                    { id: "txtUpdate_AddSeat_OTFare", label: "OT Fare", value: "0", col: 2 },
                    { id: "txtUpdate_AddSeat_InfantFare", label: "Infant Fare", value: "0", col: 3 },
                    { id: "txtUpdate_AddSeat_GrandTotalFare", label: "Grand Total", value: "0", readOnly: true, col: 3 },
                  ].map(({ id, label, value, readOnly, col }) => (
                    <div className={`col-md-${col}`} key={id}>
                      <label className="form-label mb-1" style={{ fontSize: "11px", fontWeight: "700", color: "#8a9ab5", textTransform: "uppercase", letterSpacing: "0.6px" }}>{label}</label>
                      <input type="text" className="form-control form-control-sm" id={id} value={value} readOnly={readOnly}
                        style={{ borderRadius: "8px", border: "1.5px solid #dde3ef", background: readOnly ? "#f8f9fe" : "white", fontSize: "13px" }} />
                    </div>
                  ))}
                  <div className="col-md-3 d-flex align-items-end">
                    <button type="submit" className="btn btn-sm fw-semibold d-flex align-items-center gap-2 text-white w-100 justify-content-center"
                      style={{ background: "linear-gradient(135deg, #065f46, #10b981)", borderRadius: "8px", border: "none", boxShadow: "0 4px 12px rgba(6,95,70,0.25)", fontSize: "13px", height: "34px" }}>
                      <i className="bi bi-plus-circle-fill"></i> Add Seat
                    </button>
                  </div>
                </div>
              </div>

              {/* Decrease Seat Section */}
              <div className="p-3 rounded-3" style={{ background: "#f8f9fe", border: "1px solid #eef0f8" }}>
                <div className="d-flex align-items-center gap-2 mb-3">
                  <i className="bi bi-dash-circle-fill" style={{ color: "#dc2626", fontSize: "13px" }}></i>
                  <span style={{ fontSize: "11px", fontWeight: "700", color: "#dc2626", textTransform: "uppercase", letterSpacing: "0.7px" }}>Decrease Seat</span>
                  <span style={{ fontSize: "10px", fontWeight: "700", background: "#eef0ff", color: "#1a3a8f", borderRadius: "5px", padding: "1px 7px" }}>PNR: ABC123</span>
                </div>
                <div className="row g-2 align-items-end">
                  <div className="col-md-3">
                    <label className="form-label mb-1" style={{ fontSize: "11px", fontWeight: "700", color: "#8a9ab5", textTransform: "uppercase", letterSpacing: "0.6px" }}>Select Fare</label>
                    <select className="form-select form-select-sm" id="txtUpdate_MinusSeat_SelectSeat"
                      style={{ borderRadius: "8px", border: "1.5px solid #dde3ef", fontSize: "13px" }}>
                      <option value="">Select</option>
                      <option value="5000">₹ 5,000</option>
                      <option value="2000">₹ 2,000</option>
                    </select>
                  </div>
                  <div className="col-md-2">
                    <label className="form-label mb-1" style={{ fontSize: "11px", fontWeight: "700", color: "#8a9ab5", textTransform: "uppercase", letterSpacing: "0.6px" }}>Seat Left</label>
                    <input type="text" className="form-control form-control-sm" id="txtUpdate_MinusSeat_SeatLeft" value="0" readOnly
                      style={{ borderRadius: "8px", border: "1.5px solid #dde3ef", background: "#f8f9fe", fontSize: "13px" }} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label mb-1" style={{ fontSize: "11px", fontWeight: "700", color: "#8a9ab5", textTransform: "uppercase", letterSpacing: "0.6px" }}>Seats to Decrease</label>
                    <select className="form-select form-select-sm" id="txtUpdate_MinusSeat_MinusSelectSeat"
                      style={{ borderRadius: "8px", border: "1.5px solid #dde3ef", fontSize: "13px" }}>
                      <option value="">Select</option>
                      <option value="1">1</option>
                      <option value="2">2</option>
                    </select>
                  </div>
                  <div className="col-md-3 d-flex align-items-end">
                    <button type="submit" className="btn btn-sm fw-semibold d-flex align-items-center gap-2 text-white w-100 justify-content-center"
                      style={{ background: "linear-gradient(135deg, #991b1b, #dc2626)", borderRadius: "8px", border: "none", boxShadow: "0 4px 12px rgba(220,38,38,0.25)", fontSize: "13px", height: "34px" }}>
                      <i className="bi bi-dash-circle-fill"></i> Decrease Seat
                    </button>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default ListInventory